/**
 * DecisionConflictWarnings (FLOW-577)
 *
 * Renders the warnings FLOW-573's contradiction detector raised against one
 * decision being captured or confirmed, and lets a human pick how to dispose
 * of each one. Purely presentational — the caller owns the choices, applies
 * their consequences (D3/D4), and calls `resolve`. This component makes no
 * API calls.
 *
 * `allowEdited` gates the `accepted_edited` outcome (D5): it is only true on
 * the quick-capture surface, which has an edit form to return to.
 */

import { Component, For } from "solid-js";
import type {
  ConflictResolution,
  DecisionConflictWarning,
} from "../../api/types";
import { Badge, type BadgeVariant } from "../ui";

export interface DecisionConflictWarningsProps {
  warnings: DecisionConflictWarning[];
  /** warning id -> chosen outcome, owned by the caller */
  choices: Record<string, ConflictResolution | undefined>;
  onChoose: (warningId: string, outcome: ConflictResolution) => void;
  /** offer `accepted_edited` — only true where an edit form exists (D5) */
  allowEdited: boolean;
  /** disable the controls while the caller is applying the choices */
  busy: boolean;
}

interface OutcomeOption {
  value: ConflictResolution;
  label: string;
}

const BASE_OUTCOMES: OutcomeOption[] = [
  { value: "accepted_superseded", label: "Supersede the existing decision" },
  { value: "accepted_linked", label: "Keep both, mark as related" },
  { value: "dismissed_not_a_conflict", label: "Not a conflict" },
  { value: "dismissed_intentional", label: "Intentional — keep both" },
];

const EDITED_OUTCOME: OutcomeOption = {
  value: "accepted_edited",
  label: "Let me edit this",
};

const CONFIDENCE_VARIANTS: Record<string, BadgeVariant> = {
  high: "high",
  medium: "medium",
  low: "low",
};

export const DecisionConflictWarnings: Component<
  DecisionConflictWarningsProps
> = (props) => {
  const outcomes = () =>
    props.allowEdited ? [...BASE_OUTCOMES, EDITED_OUTCOME] : BASE_OUTCOMES;

  return (
    <ul
      class="flex flex-col gap-3"
      aria-label="Possible conflicts with existing decisions"
    >
      <For each={props.warnings}>
        {(warning) => (
          <li class="rounded-md border border-signal-amber/25 bg-signal-amber-tint p-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <p class="text-sm font-semibold text-ink">
                  May conflict with &quot;{warning.existing_title}&quot;
                </p>
                <p class="mt-1 text-sm text-ink-2">{warning.explanation}</p>
              </div>
              <Badge
                variant={CONFIDENCE_VARIANTS[warning.confidence] ?? "default"}
              >
                {warning.confidence} confidence
              </Badge>
            </div>

            <div
              role="radiogroup"
              aria-label={`Resolution for conflict with "${warning.existing_title}"`}
              class="mt-3 flex flex-wrap gap-2"
            >
              <For each={outcomes()}>
                {(option) => {
                  const selected = () =>
                    props.choices[warning.id] === option.value;
                  return (
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selected()}
                      disabled={props.busy}
                      onClick={() => props.onChoose(warning.id, option.value)}
                      class={
                        "rounded-full border px-3 py-1 text-xs font-medium focus-ring disabled:cursor-not-allowed disabled:opacity-50 " +
                        (selected()
                          ? "border-ink bg-ink text-paper"
                          : "border-line bg-surface text-ink-2 hover:bg-sunken")
                      }
                    >
                      {option.label}
                    </button>
                  );
                }}
              </For>
            </div>
          </li>
        )}
      </For>
    </ul>
  );
};

export default DecisionConflictWarnings;
